import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export const usePlatformWhatsApp = () => {
  const [n, setN] = useState("");
  useEffect(() => { api.get("/platform/contact").then((r) => setN(r.data.whatsapp || "")).catch(() => {}); }, []);
  return [n, setN] as const;
};

export const ForgotPasswordWhatsApp = ({ username = "" }: { username?: string }) => {
  const { t } = usePrefs();
  const [n] = usePlatformWhatsApp();
  if (!n) return <p data-testid="partner-forgot-note" className="text-muted-foreground font-semibold">{t("auth.forgotAdmin")}</p>;
  return (
    <a href={`https://wa.me/${n}?text=${encodeURIComponent(t("auth.forgotMsg") + username.trim())}`} target="_blank" rel="noreferrer" data-testid="partner-forgot-link"
      className="inline-flex items-center gap-1.5 text-muted-foreground font-semibold hover:text-foreground">
      <MessageCircle className="h-4 w-4 text-[#25D366]" />{t("auth.forgotWa")}
    </a>
  );
};

export const FloatingWhatsApp = () => {
  const { t } = usePrefs();
  const [n] = usePlatformWhatsApp();
  if (!n) return null;
  return (
    <a href={`https://wa.me/${n}?text=${encodeURIComponent(t("pc.msg"))}`} target="_blank" rel="noreferrer" data-testid="floating-whatsapp-button" aria-label={t("pc.floating")}
      className="group fixed bottom-5 end-5 z-40 h-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-black/20 inline-flex items-center gap-2 px-4 hover:bg-[#1ebe5b] active:scale-95 transition-transform animate-in fade-in slide-in-from-bottom-4 duration-500">
      <MessageCircle className="h-6 w-6" />
      <span className="text-sm font-bold hidden sm:inline">{t("pc.floating")}</span>
    </a>
  );
};

export const AdminWhatsAppButton = () => {
  const { t } = usePrefs();
  const u = useAuth().user;
  const [n] = usePlatformWhatsApp();
  if (!n) return null;
  const role = u ? t(`role.${u.role}`) + (u.venue_role ? ` · ${t(`role.${u.venue_role}`)}` : "") : "";
  const msg = u ? t("pc.msgUser").replace("{name}", u.name || "").replace("{email}", u.email || (u.username ? `@${u.username}` : "")).replace("{role}", role)
    + (u.venue_account?.business_name ? t("pc.msgPartner").replace("{business}", u.venue_account.business_name) : "") : t("pc.msg");
  return (
    <div data-testid="platform-contact-card" className="rounded-3xl border bg-card p-5 space-y-3">
      <div>
        <p className="font-bold flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" />{t("pc.title")}</p>
        <p className="text-sm text-muted-foreground mt-1">{t("pc.sub")}</p>
      </div>
      <a href={`https://wa.me/${n}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="platform-whatsapp-button"
        className="h-11 px-5 rounded-full bg-[#25D366] text-white font-bold inline-flex items-center gap-2 hover:bg-[#1ebe5b] active:scale-95 transition-transform">
        <MessageCircle className="h-4 w-4" />{t("pc.button")}
      </a>
    </div>
  );
};

export const PlatformContactSettings = () => {
  const { t } = usePrefs();
  const [n, setN] = usePlatformWhatsApp();
  const save = async () => {
    try {
      setN((await api.put("/admin/platform/contact", { whatsapp: n })).data.whatsapp);
      toast.success(t("pc.saved"));
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  return (
    <div data-testid="platform-contact-settings" className="rounded-3xl border bg-card p-5 space-y-3">
      <p className="font-bold flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" />{t("pc.adminTitle")}</p>
      <p className="text-sm text-muted-foreground">{t("pc.adminNote")}</p>
      <div className="flex flex-wrap gap-2">
        <Input dir="ltr" inputMode="tel" value={n} onChange={(e: any) => setN(e.target.value)} placeholder="07XXXXXXXXX" data-testid="platform-whatsapp-input" className="h-10 rounded-full w-52" />
        <button onClick={save} data-testid="platform-whatsapp-save" className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold active:scale-95 transition-transform">{t("common.save")}</button>
      </div>
    </div>
  );
};
